import { TestResult } from '../types';
import { Database, dbToTestResult, testResultToDb } from './supabaseClient';
import { fetchAll, fetchById, createRecord, updateRecord, deleteRecord, getCurrentUser } from './baseService';
import { supabase, isSupabaseAvailable } from './supabaseClient';
import { trackEvent } from '../components/GoogleAnalytics';

// ============================================
// TEST RESULT SERVICE
// ============================================

type TestResultRow = Database['public']['Tables']['test_results']['Row'];

/**
 * Get all test results for the current user
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export const getTestResults = async (): Promise<TestResult[]> => {
  return fetchAll<TestResultRow, TestResult>(
    'test_results',
    dbToTestResult,
    {
      orderBy: 'date',
      ascending: false
    }
  );
};

/**
 * Get test results for a specific patient (current user's patient only)
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export const getTestResultsByPatientId = async (patientId: string): Promise<TestResult[]> => {
  return fetchAll<TestResultRow, TestResult>(
    'test_results',
    dbToTestResult,
    {
      orderBy: 'date',
      ascending: false,
      filters: [{ column: 'patient_id', value: patientId }]
    }
  );
};

/**
 * Get a single test result by ID
 */
export const getTestResultById = async (id: string): Promise<TestResult | null> => {
  return fetchById<TestResultRow, TestResult>('test_results', id, dbToTestResult);
};

/**
 * Create a new test result
 * SECURITY: Explicitly sets user_id from authenticated user
 */
export const createTestResult = async (
  resultData: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt' | 'patientName'>
): Promise<TestResult | null> => {
  const result = await createRecord<TestResultRow, TestResult, Omit<TestResult, 'id' | 'createdAt' | 'updatedAt' | 'patientName'>>(
    'test_results',
    resultData,
    testResultToDb,
    dbToTestResult
  );

  if (result) {
    trackEvent('test_completed', {
      test_code: resultData.testCode,
      test_category: resultData.testCategory
    });
  }

  return result;
};

/**
 * Update an existing test result
 */
export const updateTestResult = async (
  id: string,
  updates: Partial<Pick<TestResult, 'aiAnalysis' | 'aiAnalysisHistory' | 'answers' | 'totalScore' | 'subscores' | 'severityLabel'>>
): Promise<TestResult | null> => {
  const dbUpdates: Record<string, unknown> = {};

  if (updates.aiAnalysis !== undefined) dbUpdates.ai_analysis = updates.aiAnalysis || null;
  if (updates.aiAnalysisHistory !== undefined) dbUpdates.ai_analysis_history = updates.aiAnalysisHistory || null;
  if (updates.answers !== undefined) dbUpdates.answers = updates.answers;
  if (updates.totalScore !== undefined) dbUpdates.total_score = updates.totalScore;
  if (updates.subscores !== undefined) dbUpdates.subscores = updates.subscores || null;
  if (updates.severityLabel !== undefined) dbUpdates.severity_label = updates.severityLabel;

  return updateRecord<TestResultRow, TestResult>('test_results', id, dbUpdates, dbToTestResult);
};

/**
 * Delete a test result
 */
export const deleteTestResult = async (id: string): Promise<boolean> => {
  return deleteRecord('test_results', id);
};

/**
 * Get recent test results (last 30 days) for the current user
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export const getRecentTestResults = async (): Promise<TestResult[]> => {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not available');
    return [];
  }

  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('No authenticated user, cannot fetch test results');
      return [];
    }

    const { data, error } = await supabase!
      .from('test_results')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching recent test results:', error);
      return [];
    }

    return (data || []).map(dbToTestResult);
  } catch (err) {
    console.error('Failed to fetch recent test results:', err);
    return [];
  }
};

/**
 * Get test results by category for the current user
 * SECURITY: Filters by user_id (defense-in-depth, RLS also enforces this)
 */
export const getTestResultsByCategory = async (category: string): Promise<TestResult[]> => {
  return fetchAll<TestResultRow, TestResult>(
    'test_results',
    dbToTestResult,
    {
      orderBy: 'date',
      ascending: false,
      filters: [{ column: 'test_category', value: category }]
    }
  );
};

/**
 * Compare two test results (requires them to be from the same patient and same test type)
 */
export const compareTestResults = async (
  testId1: string,
  testId2: string
): Promise<{
  test1: TestResult;
  test2: TestResult;
  scoreDifference: number;
  percentageChange: number;
} | null> => {
  const test1 = await getTestResultById(testId1);
  const test2 = await getTestResultById(testId2);

  if (!test1 || !test2) {
    console.error('One or both test results not found');
    return null;
  }

  if (test1.patientId !== test2.patientId) {
    console.error('Test results are not from the same patient');
    return null;
  }

  if (test1.testId !== test2.testId) {
    console.error('Test results are not the same test type');
    return null;
  }

  const scoreDifference = test2.totalScore - test1.totalScore;
  const percentageChange =
    test1.totalScore > 0
      ? Math.round((scoreDifference / test1.totalScore) * 100 * 100) / 100
      : 0;

  return {
    test1,
    test2,
    scoreDifference,
    percentageChange,
  };
};
